import React, { useEffect, useState } from "react";

import { Minus, Plus, ShoppingCart, Trash2, X } from "lucide-react";
import NoImg from "../Images/Item_Image_Placeholder.webp";
import { useCart } from "../Context/CartContext";

const formatCurrency = (value) => {
  const amount = Number(value || 0);

  return `₹${Number.isFinite(amount) ? amount.toFixed(0) : "0"}`;
};

export default function CartDrawer({ open, onClose, onCheckout }) {
  const {
    items,
    itemCount,
    subtotal,
    increaseItem,
    decreaseItem,
    removeItem,
    clearCart,
  } = useCart();

  const [openCustomizationKey, setOpenCustomizationKey] = useState(null);

  const getCustomizationEntries = (item) => {
    let parsedCustomization = item?.customization || {};

    if (typeof parsedCustomization === "string") {
      try {
        parsedCustomization = JSON.parse(parsedCustomization || "{}");
      } catch {
        parsedCustomization = {};
      }
    }

    if (
      !parsedCustomization ||
      typeof parsedCustomization !== "object" ||
      Array.isArray(parsedCustomization)
    ) {
      return [];
    }

    return Object.entries(parsedCustomization).filter(
      ([, value]) => String(value || "").trim() !== "",
    );
  };

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;

    document.body.style.overflow = "hidden";

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        onClose?.();
      }
    };

    window.addEventListener("keydown", handleEscape);

    return () => {
      document.body.style.overflow = previousOverflow;

      window.removeEventListener("keydown", handleEscape);
    };
  }, [open, onClose]);

  const handleCheckout = () => {
    if (!items.length) {
      return;
    }

    onCheckout?.();
  };

  if (!open) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-950/45 backdrop-blur-[1px] sm:justify-end">
      {/* Overlay */}
      <button
        type="button"
        aria-label="Close cart"
        onClick={onClose}
        className="absolute inset-0 cursor-default"
      />

      {/* Drawer */}
      <aside
        className="
    relative z-10 flex flex-col overflow-hidden bg-white
    w-[calc(100%-26px)]
    h-[calc(100%-40px)]
    max-h-[calc(100%-40px)]
    rounded-[22px]
    shadow-[0_10px_40px_rgba(15,23,42,0.20)]

    sm:h-full
    sm:max-h-none
    sm:w-full
    sm:max-w-[480px]
    sm:rounded-l-[28px]
    sm:rounded-r-none
    sm:shadow-[-20px_0_50px_rgba(15,23,42,0.20)]
  "
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-slate-100 px-4 py-4 sm:px-7 sm:py-5">
          <div>
            <h2 className="text-[20px] font-bold text-slate-900 sm:text-[22px]">
              Your Cart
            </h2>

            <p className="mt-0.5 text-sm text-slate-500">
              {itemCount} {itemCount === 1 ? "item" : "items"}
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            aria-label="Close cart drawer"
            className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-100 text-slate-700 transition hover:bg-blue-50 hover:text-blue-700 sm:h-11 sm:w-11"
          >
            <X size={21} />
          </button>
        </div>

        {/* Cart title */}
        <div className="flex items-center justify-between px-4 pb-3 pt-4 sm:px-7 sm:pt-5">
          <h3 className="text-[15px] font-semibold text-slate-800">
            Your Cart
          </h3>

          {items.length > 0 && (
            <button
              type="button"
              onClick={clearCart}
              className="text-sm font-medium text-slate-400 transition hover:text-blue-700"
            >
              All Clear
            </button>
          )}
        </div>

        {/* Cart items */}
        <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-4 [scrollbar-width:none] sm:px-7 sm:pb-5 [&::-webkit-scrollbar]:hidden">
          {items.length === 0 ? (
            <div className="flex h-full min-h-[320px] flex-col items-center justify-center text-center">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-blue-50 text-blue-700">
                <ShoppingCart size={28} />
              </div>

              <h3 className="mt-4 text-lg font-semibold text-slate-900">
                Your cart is empty
              </h3>

              <p className="mt-1 max-w-[250px] text-sm leading-6 text-slate-500">
                Add your favourite dishes from the menu to continue.
              </p>
            </div>
          ) : (
            <div className="space-y-1">
              {items.map((item, index) => {
                const itemId = String(item?.itemId || item?.productId || "");

                const itemName = item?.name || item?.productName || "Menu item";

                const itemSize = item?.size || item?.sizeId || "Regular";

                const itemImage = item?.image || item?.itemImage || "";

                const itemPrice = item?.unitPrice || item?.itemPrice || 0;

                // =====================================================
                // UNIQUE CART ROW KEY
                // Same item + same size but different customization
                // ko separate React row milegi.
                // =====================================================

                const customizationKey =
                  typeof item?.customization === "string"
                    ? item.customization
                    : JSON.stringify(
                        item?.selectedCustomizations ||
                          item?.customization ||
                          {},
                      );

                const cartRowKey = [
                  item?.restaurantId || "",
                  itemId,
                  itemSize,
                  customizationKey,
                  index,
                ].join("::");

                return (
                  <div
                    key={cartRowKey}
                    className="grid grid-cols-[48px_minmax(0,1fr)] gap-3 border-b border-slate-100 py-3.5 sm:grid-cols-[52px_minmax(0,1fr)_auto] sm:items-center sm:py-4"
                  >
                    {/* Image */}
                    <div className="h-12 w-12 overflow-hidden rounded-xl bg-slate-100 sm:h-[52px] sm:w-[52px]">
                      <img
                        src={itemImage || NoImg}
                        alt={itemName}
                        className="h-full w-full object-cover"
                        onError={(event) => {
                          event.currentTarget.onerror = null;
                          event.currentTarget.src = NoImg;
                        }}
                      />
                    </div>

                    {/* Item details */}
                    <div className="min-w-0">
                      <div
                        className="
      flex
      min-w-0
      flex-wrap
      items-center
      gap-x-1.5
      gap-y-1

      sm:block
    "
                      >
                        <p
                          className="
        min-w-0
        text-[13px]
        font-medium
        leading-5
        text-slate-800

        sm:line-clamp-2
        sm:text-[14px]
      "
                        >
                          {itemName} ({itemSize})
                        </p>

                        {/* Customizations */}
                        {(() => {
                          const customizationEntries =
                            getCustomizationEntries(item);

                          if (customizationEntries.length === 0) {
                            return null;
                          }

                          const customizationKey = `${itemId}-${itemSize}-${JSON.stringify(
                            item?.customization || {},
                          )}`;

                          const isCustomizationOpen =
                            openCustomizationKey === customizationKey;

                          return (
                            <div className="relative w-fit sm:mt-1.5">
                              <button
                                type="button"
                                onClick={(event) => {
                                  event.stopPropagation();

                                  setOpenCustomizationKey(
                                    isCustomizationOpen
                                      ? null
                                      : customizationKey,
                                  );
                                }}
                                onMouseEnter={() =>
                                  setOpenCustomizationKey(customizationKey)
                                }
                                onMouseLeave={() =>
                                  setOpenCustomizationKey((previous) =>
                                    previous === customizationKey
                                      ? null
                                      : previous,
                                  )
                                }
                                className="
              inline-flex
              shrink-0
              items-center
              rounded-full
              border
              border-blue-200
              bg-blue-50

              px-2
              py-[3px]

              text-[8px]
              font-semibold
              leading-none
              text-blue-700

              transition
              hover:bg-blue-100

              sm:px-2.5
              sm:py-1
              sm:text-[11px]
            "
                              >
                                Customized
                              </button>

                              {isCustomizationOpen && (
                                <div
                                  className="
  absolute
  left-1/2
  top-[calc(100%+6px)]
  z-[100]

  w-[150px]
  max-w-[calc(100vw-24px)]

  -translate-x-1/2

  rounded-[12px]
  border
  border-slate-200
  bg-white
  p-3

  shadow-[0_10px_30px_rgba(15,23,42,0.18)]

  sm:left-0
  sm:w-auto
  sm:min-w-[190px]
  sm:max-w-[260px]
  sm:translate-x-0
"
                                  onMouseEnter={() =>
                                    setOpenCustomizationKey(customizationKey)
                                  }
                                  onMouseLeave={() =>
                                    setOpenCustomizationKey(null)
                                  }
                                >
                                  <p className="mb-2 text-[11px] font-bold text-slate-800">
                                    Selected Customizations
                                  </p>

                                  <div className="space-y-1.5">
                                    {customizationEntries.map(
                                      ([key, value]) => (
                                        <p
                                          key={key}
                                          className="text-[11px] leading-4 text-slate-500"
                                        >
                                          <span className="font-semibold text-slate-700">
                                            {key}:
                                          </span>{" "}
                                          {String(value)}
                                        </p>
                                      ),
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })()}
                      </div>

                      <p className="mt-1 text-xs text-slate-500">
                        {formatCurrency(itemPrice)}
                      </p>

                      {/* Mobile quantity */}
                      <div className="mt-2.5 flex items-center justify-between sm:hidden">
                        <div className="flex h-8 items-center rounded-full bg-blue-700 px-1 text-white shadow-sm">
                          <button
                            type="button"
                            onClick={() => decreaseItem(item)}
                            aria-label={`Decrease ${itemName}`}
                            className="flex h-7 w-7 items-center justify-center rounded-full transition hover:bg-white/10"
                          >
                            <Minus size={14} strokeWidth={2.5} />
                          </button>

                          <span className="min-w-6 text-center text-xs font-bold">
                            {String(item.quantity || 0).padStart(2, "0")}
                          </span>

                          <button
                            type="button"
                            onClick={() => increaseItem(item)}
                            aria-label={`Increase ${itemName}`}
                            className="flex h-7 w-7 items-center justify-center rounded-full transition hover:bg-white/10"
                          >
                            <Plus size={14} strokeWidth={2.5} />
                          </button>
                        </div>

                        <button
                          type="button"
                          onClick={() => removeItem(item)}
                          aria-label={`Remove ${itemName}`}
                          className="flex h-8 w-8 items-center justify-center rounded-full text-slate-400 transition hover:bg-red-50 hover:text-red-500"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>

                    {/* Desktop quantity */}
                    <div className="hidden items-center gap-2 sm:flex">
                      <div className="flex h-8 items-center rounded-full bg-blue-700 px-1 text-white shadow-sm">
                        <button
                          type="button"
                          onClick={() => decreaseItem(item)}
                          aria-label={`Decrease ${itemName}`}
                          className="flex h-7 w-7 items-center justify-center rounded-full transition hover:bg-white/10"
                        >
                          <Minus size={14} strokeWidth={2.5} />
                        </button>

                        <span className="min-w-6 text-center text-xs font-bold">
                          {String(item.quantity || 0).padStart(2, "0")}
                        </span>

                        <button
                          type="button"
                          onClick={() => increaseItem(item)}
                          aria-label={`Increase ${itemName}`}
                          className="flex h-7 w-7 items-center justify-center rounded-full transition hover:bg-white/10"
                        >
                          <Plus size={14} strokeWidth={2.5} />
                        </button>
                      </div>

                      <button
                        type="button"
                        onClick={() => removeItem(item)}
                        aria-label={`Remove ${itemName}`}
                        className="flex h-8 w-8 items-center justify-center rounded-full text-slate-400 transition hover:bg-red-50 hover:text-red-500"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        {items.length > 0 && (
          <div className="border-t border-slate-100 bg-white px-4 pb-[max(14px,env(safe-area-inset-bottom))] pt-3.5 sm:px-7 sm:pt-4">
            <div className="flex items-center justify-between rounded-2xl bg-slate-50 px-4 py-3.5 sm:py-4">
              <span className="text-sm text-slate-600">Subtotal</span>

              <span className="text-base font-bold text-slate-900">
                {formatCurrency(subtotal)}
              </span>
            </div>

            <button
              type="button"
              onClick={handleCheckout}
              className="mt-3 flex h-10 w-full items-center justify-center rounded-2xl bg-blue-700 text-sm font-bold text-white shadow-[0_10px_24px_rgba(29,78,216,0.28)] transition hover:bg-blue-800 active:scale-[0.99] sm:h-14 sm:text-base"
            >
              Checkout
            </button>
          </div>
        )}
      </aside>
    </div>
  );
}
